/**
 * Backend Error Reporter Service
 * Handles error reporting to backend API with proper security
 * Data sanitization is handled by dataSanitizer.js
 * Main logging is handled by logger.js
 */

import { sanitizeLogContext } from '../utils/dataSanitizer.js';
import apiService from '../api/apiService.js';

// Rate limiting for error reporting (security measure)
const ERROR_REPORT_LIMIT = 5; // Max 5 errors per minute
const ERROR_REPORT_WINDOW = 60 * 1000; // 1 minute
const ANONYMOUS_ERROR_LIMIT = 3; // Max 3 errors per minute for anonymous users
let errorReportCount = 0;
let anonymousErrorCount = 0;
let errorReportWindowStart = Date.now();
let anonymousWindowStart = Date.now();

// Browser fingerprint for anonymous user identification
const getBrowserFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Browser fingerprint', 2, 2);
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    new Date().getTimezoneOffset(),
    canvas.toDataURL()
  ].join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Check if error reporting is rate limited
 */
const isRateLimited = (isAnonymous = false) => {
  const now = Date.now();
  
  if (isAnonymous) {
    // Reset anonymous window if expired
    if (now - anonymousWindowStart > ERROR_REPORT_WINDOW) {
      anonymousErrorCount = 0;
      anonymousWindowStart = now;
    }
    return anonymousErrorCount >= ANONYMOUS_ERROR_LIMIT;
  } else {
    // Reset authenticated window if expired
    if (now - errorReportWindowStart > ERROR_REPORT_WINDOW) {
      errorReportCount = 0;
      errorReportWindowStart = now;
    }
    return errorReportCount >= ERROR_REPORT_LIMIT;
  }
};

/**
 * Send error to backend API with security measures
 * Supports both authenticated and anonymous users with different endpoints
 */
export const sendErrorToBackend = async (error, userContext = {}, additionalContext = {}) => {
  try {
    const token = localStorage.getItem('token');
    const isAnonymous = !token;
    
    // Rate limiting check (prevent spam)
    if (isRateLimited(isAnonymous)) {
      console.debug(`Error reporting rate limited for ${isAnonymous ? 'anonymous' : 'authenticated'} user`);
      return;
    }
    
    // Additional client-side rate limiting for anonymous users
    if (isAnonymous && isClientRateLimited()) {
      console.debug('Anonymous error reporting client-side rate limited');
      return;
    }
    
    // Increment appropriate rate limit counter
    if (isAnonymous) {
      anonymousErrorCount++;
      clientAnonymousCount++;
    } else {
      errorReportCount++;
    }
    
    const sanitizedData = sanitizeLogContext({
      message: error.message,
      stack: error.stack,
      name: error.name,
      ...additionalContext
    });

    const errorLog = {
      message: sanitizedData.message,
      stack: sanitizedData.stack,
      name: sanitizedData.name,
      user: userContext,
      browser: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform
      },
      page: {
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer
      },
      context: sanitizedData,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'development'
    };

    // Choose endpoint and method based on authentication status
    try {
      let response;
      
      if (isAnonymous) {
        // Anonymous error logging with browser fingerprint
        response = await fetch('/api/v1/logs/error/anonymous', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Browser-Fingerprint': getBrowserFingerprint(),
            'X-User-Type': 'anonymous',
            'X-Session-Id': getSessionId()
          },
          body: JSON.stringify({
            ...errorLog,
            user: { 
              type: 'anonymous', 
              sessionId: getSessionId(),
              fingerprint: getBrowserFingerprint()
            }
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } else {
        // Authenticated error logging with CSRF
        response = await apiService.post('/api/v1/logs/error', errorLog);
      }
      
      console.debug(`Error successfully reported to backend (${isAnonymous ? 'anonymous' : 'authenticated'})`);
      return response;
    } catch (apiError) {
      // Silently handle failures to prevent error loops
      console.debug(`Backend error logging failed (${isAnonymous ? 'anonymous' : 'authenticated'}):`, 
        apiError.status || apiError.message || 'unknown'
      );
    }
  } catch (loggingError) {
    // Don't let error logging cause more errors
    console.debug('Error while logging to backend:', loggingError.message);
  }
};

/**
 * Get or create a session ID for anonymous users
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('error_session_id');
  if (!sessionId) {
    sessionId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('error_session_id', sessionId);
  }
  return sessionId;
};

// Client-side rate limiting for anonymous users
const ANONYMOUS_CLIENT_LIMIT = 2; // Max 2 errors per minute client-side
let clientAnonymousCount = 0;
let clientAnonymousWindow = Date.now();

const isClientRateLimited = () => {
  const now = Date.now();
  if (now - clientAnonymousWindow > ERROR_REPORT_WINDOW) {
    clientAnonymousCount = 0;
    clientAnonymousWindow = now;
  }
  return clientAnonymousCount >= ANONYMOUS_CLIENT_LIMIT;
};

const backendErrorReporter = {
  sendErrorToBackend
};

export default backendErrorReporter;
